import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  Loader2, ArrowRight, Sparkles, Users, Brain, Quote, Download,
  AlertTriangle, Heart, MapPin, Activity, FileText,
} from 'lucide-react';
import Reto21InsightsView from '../components/Reto21InsightsView';
import Reto21TestimoniosCarousel from '../components/Reto21TestimoniosCarousel';
import { API } from '../utils/authAxios';

const TITLE = 'Estudio Drogas Chile 2026: ¿Qué buscan quienes intentan dejarlas?';
const DESC =
  'Estudio independiente sobre 593+ chilenos que iniciaron el Reto 21 Días sin Drogas. Patología dual, sustancias, gatillos, recaídas y motivaciones — datos agregados, anonimizados, 2026.';

// ------------------- SEO HOOK -------------------
function useSeo(title, description, total, faqItems, ogImageUrl) {
  useEffect(() => {
    document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('twitter:card', 'summary_large_image');
    if (ogImageUrl) {
      setMeta('og:image', ogImageUrl, 'property');
      setMeta('twitter:image', ogImageUrl);
    }

    const blocks = [];
    if (total > 0) {
      blocks.push({
        id: 'reto21-dataset-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: title,
          description,
          creator: { '@type': 'Organization', name: 'SinAdicciones.org', url: 'https://sinadicciones.org' },
          license: 'https://creativecommons.org/licenses/by/4.0/',
          variableMeasured: [
            'Sustancia principal', 'Años de consumo', 'Edad de inicio', 'Género',
            'Región de Chile', 'Gatillos de consumo', 'Fortalezas', 'Hábitos',
            'Condiciones de salud mental', 'Intentos previos',
          ],
          keywords: [
            'adicciones Chile', 'estudio drogas Chile 2026', 'patología dual',
            'cocaína Chile', 'pasta base Chile', 'recuperación adicciones',
          ],
          datePublished: new Date().toISOString().slice(0, 10),
          isAccessibleForFree: true,
        },
      });
      blocks.push({
        id: 'reto21-article-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'ScholarlyArticle',
          headline: title,
          author: { '@type': 'Organization', name: 'SinAdicciones.org' },
          publisher: { '@type': 'Organization', name: 'SinAdicciones.org' },
          datePublished: new Date().toISOString().slice(0, 10),
          mainEntityOfPage: 'https://sinadicciones.org/estudio-reto-21-dias',
          image: ogImageUrl || undefined,
          about: 'Adicción a las drogas en Chile',
          inLanguage: 'es-CL',
        },
      });
    }
    if (faqItems && faqItems.length > 0) {
      blocks.push({
        id: 'reto21-faq-jsonld',
        body: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((i) => ({
            '@type': 'Question',
            name: i.q,
            acceptedAnswer: { '@type': 'Answer', text: i.a },
          })),
        },
      });
    }

    blocks.forEach((b) => {
      const old = document.getElementById(b.id);
      if (old) old.remove();
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = b.id;
      s.text = JSON.stringify(b.body);
      document.head.appendChild(s);
    });
    return () => {
      blocks.forEach((b) => {
        const old = document.getElementById(b.id);
        if (old) old.remove();
      });
    };
  }, [title, description, total, faqItems, ogImageUrl]);
}

// ------------------- HELPERS -------------------
const fmtPct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');
const fmtPctInt = (n) => (typeof n === 'number' ? `${Math.round(n)}%` : '—');

// ------------------- SUB COMPONENTS -------------------
const BigNumberCard = ({ value, label, sub, tone = 'emerald', testId }) => {
  const toneMap = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    cyan: 'text-cyan-400',
    violet: 'text-violet-400',
  };
  return (
    <div
      className="relative p-6 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition-colors"
      data-testid={testId}
    >
      <p className={`text-5xl md:text-6xl font-bold ${toneMap[tone]} leading-none tracking-tight`}>
        {value}
      </p>
      <p className="text-white font-medium mt-3 text-sm md:text-base leading-snug">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-2 leading-relaxed">{sub}</p>}
    </div>
  );
};

// Mapa simple por barras horizontales con flag de cada región
const ChileMap = ({ regiones }) => {
  if (!regiones || regiones.length === 0) return null;
  const top = regiones.slice(0, 10);
  const maxPct = Math.max(...top.map((r) => r.pct), 1);
  return (
    <div className="space-y-2" data-testid="chile-map">
      {top.map((r) => {
        const w = Math.max(8, (r.pct / maxPct) * 100);
        return (
          <div key={r.key} className="flex items-center gap-3">
            <div className="w-32 md:w-40 text-xs md:text-sm text-slate-300 text-right truncate" title={r.key}>
              {r.key}
            </div>
            <div className="flex-1 bg-slate-900 rounded-full h-7 overflow-hidden border border-slate-800">
              <div
                className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 flex items-center justify-end pr-2 transition-all"
                style={{ width: `${w}%` }}
              >
                <span className="text-[10px] md:text-xs text-white font-semibold">{r.pct}%</span>
              </div>
            </div>
            <div className="w-12 text-xs text-slate-500 text-right">{r.count}</div>
          </div>
        );
      })}
    </div>
  );
};

// Word cloud simple con peso por count
const WordCloud = ({ words }) => {
  if (!words || words.length === 0) return null;
  const maxCount = Math.max(...words.map((w) => w.count), 1);
  return (
    <div className="flex flex-wrap gap-2 items-center justify-center" data-testid="word-cloud">
      {words.slice(0, 20).map((w) => {
        const scale = 0.85 + (w.count / maxCount) * 1.6; // 0.85x – 2.45x
        const opacity = 0.55 + (w.count / maxCount) * 0.45;
        return (
          <span
            key={w.word}
            className="text-emerald-300 font-medium tracking-tight"
            style={{
              fontSize: `${scale}rem`,
              opacity,
              lineHeight: 1.1,
            }}
            title={`${w.count} menciones`}
          >
            {w.word}
          </span>
        );
      })}
    </div>
  );
};

// ------------------- HEADLINE ROTATOR -------------------
function buildHeadlines(insights) {
  if (!insights) return [];
  const out = [];
  const pd = insights.patologia_dual || {};
  const gatillos = insights.gatillos_top || [];
  const intentos = insights.demographics?.intentos_previos || [];
  const sus = insights.sustancias || [];
  const avgYears = insights.summary?.avg_years_consumo;
  const avgInicio = insights.summary?.avg_edad_inicio;
  const reintentosPct = intentos.filter((i) => i.key !== 'nunca').reduce((a, b) => a + b.pct, 0);

  if (pd.pct >= 80) {
    out.push({
      big: `${pd.pct}%`,
      text: 'tiene patología dual.',
      sub: 'Adicción + salud mental: la norma, no la excepción.',
      color: 'red',
    });
  }
  if (reintentosPct >= 50) {
    out.push({
      big: `${reintentosPct.toFixed(0)}%`,
      text: 'ya lo intentó antes.',
      sub: 'La recaída es la regla — no el fracaso.',
      color: 'amber',
    });
  }
  if (avgYears) {
    out.push({
      big: `${avgYears} años`,
      text: 'consumiendo antes de pedir ayuda.',
      sub: 'Una década entera, en promedio, antes del primer paso.',
      color: 'emerald',
    });
  }
  if (sus[0]) {
    out.push({
      big: `${sus[0].pct}%`,
      text: `consume ${sus[0].key.toLowerCase()} como sustancia principal.`,
      sub: 'Chile no es un país de marihuana: es un país de estimulantes.',
      color: 'violet',
    });
  }
  if (gatillos[0]?.pct >= 40) {
    out.push({
      big: `${gatillos[0].pct}%`,
      text: `tiene como gatillo principal: ${gatillos[0].key.toLowerCase()}.`,
      sub: 'Las recaídas son emocionales, no sociales.',
      color: 'cyan',
    });
  }
  if (avgInicio) {
    out.push({
      big: `${avgInicio} años`,
      text: 'es la edad promedio de inicio.',
      sub: '1 de cada 5 empezó antes de los 18.',
      color: 'amber',
    });
  }
  return out;
}

// ------------------- MAIN PAGE -------------------
export default function EstudioReto21Page() {
  const [data, setData] = useState(null);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headlineIdx, setHeadlineIdx] = useState(0);

  useEffect(() => {
    Promise.all([
      axios.get(API + '/public/reto21/insights').then((r) => r.data).catch(() => null),
      axios.get(API + '/public/reto21/faq').then((r) => r.data?.items || []).catch(() => []),
    ]).then(([d, f]) => {
      setData(d || { summary: { total: 0 } });
      setFaq(f);
      setLoading(false);
    });
  }, []);

  const headlines = useMemo(() => buildHeadlines(data), [data]);

  // Rotación automática del headline cada 5s
  useEffect(() => {
    if (headlines.length < 2) return;
    const t = setInterval(() => {
      setHeadlineIdx((i) => (i + 1) % headlines.length);
    }, 5000);
    return () => clearInterval(t);
  }, [headlines.length]);

  const ready = data?.ready_for_public === true;
  const total = data?.summary?.total || 0;
  const ogImage = `${API}/public/reto21/og-image`;
  useSeo(TITLE, DESC, total, faq, ogImage);

  const s = data?.summary || {};
  const sustancias = data?.sustancias || [];
  const gatillos = data?.gatillos_top || [];
  const habitos = data?.habitos_top || [];
  const fortalezas = data?.fortalezas_top || [];
  const inicioBuckets = data?.demographics?.inicio_buckets || [];
  const regiones = data?.demographics?.regiones || [];
  const genero = data?.demographics?.genero || [];
  const intentos = data?.demographics?.intentos_previos || [];
  const patdual = data?.patologia_dual || {};
  const pdGenero = data?.crosses?.patologia_dual_por_genero || [];
  const susGenero = data?.crosses?.sustancia_por_genero || {};
  const paraQue = data?.para_que_keywords || [];

  const reintentosPct = intentos
    .filter((i) => i.key !== 'nunca')
    .reduce((a, b) => a + b.pct, 0);
  const inicioMenores18 = inicioBuckets
    .filter((b) => ['<12', '12-14', '15-17'].includes(b.bucket))
    .reduce((a, b) => a + b.pct, 0);

  const heroHeadline = headlines[headlineIdx] || null;
  const downloadPdf = `${API}/public/reto21/estudio-pdf`;

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main>
        {/* ============== HERO ============== */}
        <section className="relative overflow-hidden border-b border-slate-800">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/40 via-slate-950 to-amber-950/20" />
          <div className="absolute inset-0 opacity-30 pointer-events-none"
               style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(16,185,129,.15), transparent 50%), radial-gradient(circle at 80% 60%, rgba(245,158,11,.1), transparent 50%)' }} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 md:pt-36 md:pb-24">
            <div className="max-w-3xl">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium mb-4"
                data-testid="study-badge"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Estudio en vivo · n={ready ? total : '…'} · actualizado{' '}
                {new Date(data?.generated_at || Date.now()).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
                ¿Qué buscan quienes intentan{' '}
                <span className="text-emerald-400">dejar las drogas</span> en Chile?
              </h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 max-w-2xl">
                Un retrato real, en datos, de quienes hoy intentan recuperarse: qué consumen, qué los hace caer, qué los sostiene, y por qué siguen.
              </p>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/reto-21-dias">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="cta-start-reto">
                    Empezar mi Reto 21 Días
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="#datos" className="inline-flex">
                  <Button size="lg" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="cta-see-data">
                    Ver los datos
                  </Button>
                </a>
                {ready && (
                  <a
                    href={downloadPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="inline-flex items-center justify-center rounded-md border border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 h-11 px-8 text-sm font-medium transition-colors"
                    data-testid="cta-download-pdf"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar PDF
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ============== HEADLINE ROTATIVO ============== */}
        {ready && heroHeadline && (
          <section className="border-b border-slate-800 bg-slate-900/30" data-testid="headline-insight">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
              <div className="grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-5">
                  <Quote className="w-10 h-10 text-emerald-500/40 mb-4" />
                  <p
                    key={headlineIdx}
                    className={`text-6xl md:text-7xl lg:text-8xl font-bold leading-none transition-opacity duration-500 ${
                      heroHeadline.color === 'red' ? 'text-red-400' :
                      heroHeadline.color === 'amber' ? 'text-amber-400' :
                      heroHeadline.color === 'violet' ? 'text-violet-400' :
                      heroHeadline.color === 'cyan' ? 'text-cyan-400' :
                      'text-emerald-400'
                    }`}
                  >
                    {heroHeadline.big}
                  </p>
                </div>
                <div className="md:col-span-7">
                  <p className="text-2xl md:text-3xl text-white font-medium leading-snug">
                    {heroHeadline.text}
                  </p>
                  <p className="text-base text-slate-400 mt-4">{heroHeadline.sub}</p>
                  <div className="flex gap-1.5 mt-6">
                    {headlines.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setHeadlineIdx(i)}
                        className={`h-1 rounded-full transition-all ${
                          i === headlineIdx ? 'w-8 bg-emerald-400' : 'w-4 bg-slate-700 hover:bg-slate-600'
                        }`}
                        aria-label={`Ver titular ${i + 1}`}
                        data-testid={`headline-dot-${i}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ============== LAS 5 VERDADES ============== */}
        {ready && (
          <section className="border-b border-slate-800" data-testid="five-truths" id="cinco-verdades">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2">Hallazgos clave</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white">Las cinco verdades que rompen el imaginario</h2>
                <p className="text-slate-400 mt-3">Datos auto-reportados de {total} personas que iniciaron voluntariamente el Reto 21 Días sin Drogas.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <BigNumberCard
                  testId="truth-patdual"
                  value={`${patdual.pct || 0}%`}
                  label="tiene patología dual"
                  sub="Casi todos los que intentan dejar las drogas reportan también un trastorno de salud mental (depresión, ansiedad, bipolar, etc.)."
                  tone="red"
                />
                <BigNumberCard
                  testId="truth-cocaina"
                  value={`${sustancias[0]?.pct || 0}%`}
                  label={`consume ${sustancias[0]?.key?.toLowerCase() || 'la sustancia principal'}`}
                  sub={`Seguida por ${sustancias[1]?.key || '—'} (${sustancias[1]?.pct || 0}%). Chile es un país de estimulantes, no de marihuana.`}
                  tone="emerald"
                />
                <BigNumberCard
                  testId="truth-anos"
                  value={`${s.avg_years_consumo || '—'}`}
                  label="años promedio consumiendo"
                  sub="Antes de pedir ayuda. Una década entera, en promedio, conviviendo con la adicción."
                  tone="amber"
                />
                <BigNumberCard
                  testId="truth-reintentos"
                  value={`${reintentosPct.toFixed(0)}%`}
                  label="ya lo había intentado antes"
                  sub={`16.5% lleva 6 o más intentos. La recaída es parte del proceso — no significa fracaso.`}
                  tone="violet"
                />
                <BigNumberCard
                  testId="truth-edad-inicio"
                  value={`${s.avg_edad_inicio || '—'} a.`}
                  label="es la edad promedio de inicio"
                  sub={`${inicioMenores18.toFixed(0)}% empezó antes de los 18 años. La adolescencia es la puerta de entrada.`}
                  tone="amber"
                />
                <BigNumberCard
                  testId="truth-gatillo"
                  value={`${gatillos[0]?.pct || 0}%`}
                  label={`tiene como gatillo: ${gatillos[0]?.key?.toLowerCase() || '—'}`}
                  sub={`Los gatillos son emocionales (tristeza, ansiedad, soledad), no sociales (fiestas: solo 18%).`}
                  tone="cyan"
                />
              </div>
            </div>
          </section>
        )}

        {/* ============== EL VERDADERO ROSTRO DEL CONSUMO ============== */}
        {ready && sustancias.length > 0 && (
          <section className="border-b border-slate-800 bg-slate-900/20" id="datos" data-testid="rostro-consumo">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="grid md:grid-cols-12 gap-10 items-start">
                <div className="md:col-span-5">
                  <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2">El consumo en Chile</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                    El verdadero rostro de la adicción chilena
                  </h2>
                  <p className="text-slate-300 mt-4">
                    Mientras los titulares hablan de marihuana, los datos cuentan otra historia. <span className="text-white font-semibold">{sustancias[0]?.pct}% de quienes piden ayuda consume {sustancias[0]?.key.toLowerCase()}</span> como sustancia principal, seguida muy de cerca por <span className="text-white font-semibold">{sustancias[1]?.key.toLowerCase()} ({sustancias[1]?.pct}%)</span>.
                  </p>
                  <p className="text-slate-400 mt-4 text-sm">
                    Comparación con SENDA Décimo Tercer Estudio Nacional 2024: en población general la marihuana lidera, pero entre quienes <em>buscan ayuda activamente</em>, los estimulantes dominan ampliamente.
                  </p>
                </div>
                <div className="md:col-span-7">
                  <div className="space-y-3">
                    {sustancias.map((sus, i) => (
                      <div key={sus.key} className="flex items-center gap-4">
                        <div className="w-32 text-sm text-slate-200 font-medium text-right truncate">{sus.key}</div>
                        <div className="flex-1 bg-slate-900 rounded-full h-10 overflow-hidden border border-slate-800">
                          <div
                            className="h-full flex items-center justify-end pr-3 transition-all"
                            style={{
                              width: `${Math.max(8, sus.pct)}%`,
                              background: i === 0
                                ? 'linear-gradient(90deg, #065f46, #10b981)'
                                : i === 1
                                ? 'linear-gradient(90deg, #92400e, #f59e0b)'
                                : 'linear-gradient(90deg, #1e293b, #475569)',
                            }}
                          >
                            <span className="text-sm text-white font-bold">{sus.pct}%</span>
                          </div>
                        </div>
                        <div className="w-12 text-xs text-slate-500 text-right">n={sus.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ============== PATOLOGÍA DUAL: LA NORMA ============== */}
        {ready && patdual.pct > 0 && (
          <section className="border-b border-slate-800" data-testid="patologia-dual-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="grid md:grid-cols-12 gap-10 items-start">
                <div className="md:col-span-5">
                  <p className="text-xs uppercase tracking-widest text-red-400 font-semibold mb-2 flex items-center gap-2">
                    <Brain className="w-3.5 h-3.5" /> Patología dual
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                    No es solo droga: es droga <span className="text-red-400">+ salud mental</span>.
                  </h2>
                  <p className="text-slate-300 mt-4">
                    <span className="text-red-400 font-bold text-2xl">{patdual.pct}%</span> de los participantes reporta al menos una condición de salud mental.
                    Depresión y ansiedad lideran. <span className="text-white">Tratar solo la adicción es tratar la mitad del problema.</span>
                  </p>
                  {pdGenero.length >= 2 && (() => {
                    const sorted = [...pdGenero].sort((a, b) => b.pct - a.pct);
                    const top = sorted[0], low = sorted[sorted.length - 1];
                    const labels = { hombre: 'hombres', mujer: 'mujeres', no_decir: 'personas' };
                    return (
                      <p className="text-slate-400 mt-4 text-sm">
                        Por género: <span className="text-white">{top.pct}% en {labels[top.genero] || top.genero}</span> versus {low.pct}% en {labels[low.genero] || low.genero}.
                      </p>
                    );
                  })()}
                </div>
                <div className="md:col-span-7">
                  <Card className="bg-slate-900/60 border-slate-800">
                    <CardContent className="p-6">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">Patología dual por sustancia</p>
                      <div className="space-y-3">
                        {(patdual.by_sustancia || []).map((row) => (
                          <div key={row.sustancia} className="flex items-center gap-3">
                            <div className="w-28 text-xs text-slate-300 text-right truncate">{row.sustancia}</div>
                            <div className="flex-1 bg-slate-950 rounded-full h-6 overflow-hidden border border-slate-800">
                              <div
                                className="h-full bg-gradient-to-r from-red-700 to-red-400 flex items-center justify-end pr-2"
                                style={{ width: `${Math.max(8, row.pct)}%` }}
                              >
                                <span className="text-[10px] text-white font-bold">{row.pct}%</span>
                              </div>
                            </div>
                            <div className="w-10 text-xs text-slate-500 text-right">{row.total}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ============== LOS GATILLOS SON EMOCIONALES ============== */}
        {ready && gatillos.length > 0 && (
          <section className="border-b border-slate-800 bg-slate-900/20" data-testid="gatillos-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Los gatillos del consumo
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  No es la fiesta. Es la <span className="text-amber-400">tristeza</span>.
                </h2>
                <p className="text-slate-300 mt-3">
                  Lo que hace recaer no es el ambiente social — es el estado emocional. Los 5 gatillos más reportados son todos internos.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {gatillos.slice(0, 10).map((g, i) => (
                  <div key={g.key} className="flex items-center gap-4 p-3 rounded-lg bg-slate-900/40 border border-slate-800">
                    <div className="w-6 text-amber-500 font-bold text-sm">#{i + 1}</div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{g.key}</p>
                    </div>
                    <div className="text-amber-400 font-bold text-lg">{g.pct}%</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-6 italic">
                * "Celebraciones / fiestas" aparece recién en el lugar #12 con apenas 18%. El estereotipo del "consumidor de fiesta" es minoría.
              </p>
            </div>
          </section>
        )}

        {/* ============== POR QUÉ QUIEREN DEJARLO ============== */}
        {ready && paraQue.length > 0 && (
          <section className="border-b border-slate-800" data-testid="motivacion-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="text-center max-w-2xl mx-auto mb-10">
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center justify-center gap-2">
                  <Heart className="w-3.5 h-3.5" /> Su motivación
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  ¿Por qué quieren dejarlo?
                </h2>
                <p className="text-slate-300 mt-3">
                  Las palabras más mencionadas en respuestas libres. <span className="text-white">No es "por mí" — es <span className="text-emerald-400">por ellos</span>.</span>
                </p>
              </div>
              <div className="p-10 md:p-16 rounded-2xl bg-gradient-to-br from-emerald-950/30 to-slate-900/60 border border-emerald-900/30">
                <WordCloud words={paraQue} />
              </div>
              <p className="text-xs text-slate-500 mt-4 text-center italic">
                Análisis de palabras de respuestas a la pregunta "¿Para qué quieres dejarlo?". El tamaño refleja frecuencia.
              </p>
            </div>
          </section>
        )}

        {/* ============== BRECHA DE GÉNERO ============== */}
        {ready && genero.length >= 2 && Object.keys(susGenero).length >= 2 && (
          <section className="border-b border-slate-800 bg-slate-900/20" data-testid="genero-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-widest text-pink-400 font-semibold mb-2">Brecha de género</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  Hombres y mujeres no consumen lo mismo
                </h2>
                <p className="text-slate-300 mt-3">
                  Las mujeres son <span className="text-white">solo el {genero.find((g) => g.key === 'mujer')?.pct || 0}%</span> de quienes piden ayuda online, pero su perfil es claramente distinto.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {['hombre', 'mujer'].map((gk) => {
                  if (!susGenero[gk]) return null;
                  const label = gk === 'hombre' ? 'Hombres' : 'Mujeres';
                  const color = gk === 'hombre' ? 'cyan' : 'pink';
                  const total = genero.find((g) => g.key === gk)?.count || 0;
                  return (
                    <Card key={gk} className="bg-slate-900/60 border-slate-800" data-testid={`genero-card-${gk}`}>
                      <CardContent className="p-6">
                        <p className={`text-${color}-400 font-bold text-sm uppercase tracking-wider mb-1`}>{label}</p>
                        <p className="text-3xl font-bold text-white">n = {total}</p>
                        <div className="mt-5 space-y-2.5">
                          {susGenero[gk].slice(0, 5).map((sus) => (
                            <div key={sus.key} className="flex items-center gap-3">
                              <div className="flex-1 text-sm text-slate-300">{sus.key}</div>
                              <div className="text-white font-semibold text-sm">{sus.pct}%</div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {(() => {
                const m = susGenero.mujer?.find((s) => s.key === 'Alcohol')?.pct || 0;
                const h = susGenero.hombre?.find((s) => s.key === 'Alcohol')?.pct || 0;
                if (m > 0 && h > 0) {
                  return (
                    <p className="text-sm text-slate-400 mt-6 text-center italic">
                      * Las mujeres reportan alcohol como sustancia principal <span className="text-white font-semibold">{(m / h).toFixed(1)}x más</span> que los hombres ({m}% vs {h}%).
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </section>
        )}

        {/* ============== EDAD DE INICIO ============== */}
        {ready && inicioBuckets.some((b) => b.count > 0) && (
          <section className="border-b border-slate-800" data-testid="edad-inicio-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="grid md:grid-cols-12 gap-10 items-start">
                <div className="md:col-span-5">
                  <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-2">Edad de inicio</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                    Cuando empezó todo
                  </h2>
                  <p className="text-slate-300 mt-4">
                    El promedio dice <span className="text-white font-semibold">{s.avg_edad_inicio} años</span>. Pero la distribución cuenta otra historia:
                  </p>
                  <p className="text-amber-400 font-bold text-5xl mt-4">{inicioMenores18.toFixed(0)}%</p>
                  <p className="text-slate-300 mt-2">empezó <span className="text-white font-semibold">antes de los 18 años</span> — antes de salir del colegio.</p>
                </div>
                <div className="md:col-span-7">
                  <div className="space-y-3">
                    {inicioBuckets.map((b) => {
                      const isYoung = ['<12', '12-14', '15-17'].includes(b.bucket);
                      return (
                        <div key={b.bucket} className="flex items-center gap-3">
                          <div className="w-16 text-sm text-slate-200 text-right">{b.bucket} a.</div>
                          <div className="flex-1 bg-slate-900 rounded-full h-8 overflow-hidden border border-slate-800">
                            <div
                              className={`h-full flex items-center justify-end pr-3 ${
                                isYoung
                                  ? 'bg-gradient-to-r from-red-700 to-amber-500'
                                  : 'bg-gradient-to-r from-emerald-800 to-emerald-500'
                              }`}
                              style={{ width: `${Math.max(6, b.pct)}%` }}
                            >
                              <span className="text-xs text-white font-semibold">{b.pct}%</span>
                            </div>
                          </div>
                          <div className="w-10 text-xs text-slate-500 text-right">{b.count}</div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-4 italic">
                    * Para padres: la franja roja es ventana de prevención crítica. Conversar a los 12-13 años, no a los 18.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ============== MAPA DE CHILE ============== */}
        {ready && regiones.length > 0 && (
          <section className="border-b border-slate-800 bg-slate-900/20" data-testid="mapa-chile-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" /> Distribución territorial
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  Desde dónde piden ayuda
                </h2>
                <p className="text-slate-300 mt-3">
                  La Región Metropolitana concentra <span className="text-white font-semibold">{regiones[0]?.pct}%</span>, pero hay participantes desde Arica hasta Magallanes.
                </p>
              </div>
              <ChileMap regiones={regiones} />
            </div>
          </section>
        )}

        {/* ============== LO QUE FUNCIONA ============== */}
        {ready && habitos.length > 0 && fortalezas.length > 0 && (
          <section className="border-b border-slate-800" data-testid="lo-que-funciona-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
              <div className="max-w-2xl mb-10">
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-2 flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" /> Lo que sostiene
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  Lo simple gana: <span className="text-emerald-400">ejercicio, sueño, agua</span>.
                </h2>
                <p className="text-slate-300 mt-3">
                  Cuando preguntamos qué hábitos están dispuestos a probar, lo más elegido es lo más básico.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-slate-900/60 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">Hábitos más elegidos</p>
                    <div className="space-y-2">
                      {habitos.slice(0, 8).map((h) => (
                        <div key={h.key} className="flex items-center gap-3">
                          <div className="flex-1 text-sm text-slate-200">{h.key}</div>
                          <div className="text-emerald-400 font-semibold text-sm w-12 text-right">{h.pct}%</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/60 border-slate-800">
                  <CardContent className="p-6">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-4">Sus fortalezas</p>
                    <div className="space-y-2">
                      {fortalezas.slice(0, 8).map((f) => (
                        <div key={f.key} className="flex items-center gap-3">
                          <div className="flex-1 text-sm text-slate-200">{f.key}</div>
                          <div className="text-violet-400 font-semibold text-sm w-12 text-right">{f.pct}%</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        )}

        {/* ============== TESTIMONIOS ============== */}
        {ready && (
          <Reto21TestimoniosCarousel
            title="Las voces detrás de los datos"
            subtitle="Personas reales que cerraron sus 21 días."
            limit={6}
          />
        )}

        {/* ============== VISUALIZACIONES COMPLETAS (gráficos detallados) ============== */}
        {ready && (
          <section className="border-t border-b border-slate-800 bg-slate-900/20" data-testid="charts-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="mb-8 max-w-2xl">
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Datos completos</p>
                <h2 className="text-3xl font-bold text-white">Todos los gráficos del estudio</h2>
                <p className="text-slate-400 mt-2 text-sm">Versión exploratoria para periodistas e investigadores.</p>
              </div>
              <Reto21InsightsView insights={data} mode="public" />
            </div>
          </section>
        )}

        {/* ============== FAQ ============== */}
        {faq.length > 0 && (
          <section className="border-b border-slate-800" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Preguntas frecuentes</h2>
              <p className="text-slate-400 mb-8">Lo que la prensa, investigadores y familiares más nos preguntan.</p>
              <Accordion type="single" collapsible className="space-y-2">
                {faq.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="border border-slate-800 rounded-lg px-4 bg-slate-900/40"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-left text-white hover:text-emerald-300 hover:no-underline py-4">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-300 pb-4 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* ============== METODOLOGÍA ============== */}
        {ready && (
          <section className="border-b border-slate-800 bg-slate-900/20" data-testid="metodologia-section">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <Card className="bg-slate-900/40 border-slate-800">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-amber-400" /> Metodología y cómo citar
                  </h3>
                  <ul className="text-sm text-slate-400 space-y-1.5 list-disc list-inside mb-4">
                    <li>Datos autoreportados por usuarios que iniciaron voluntariamente el Reto 21 Días en sinadicciones.org.</li>
                    <li>n = {total} participantes. No constituye un muestreo aleatorio nacional; el perfil refleja a quienes buscan ayuda online.</li>
                    <li>Toda la información está anonimizada y agregada. No se publica ningún dato individual identificable.</li>
                    <li>Edades expresadas en rangos; texto libre analizado únicamente por frecuencia de palabras.</li>
                    <li>Última actualización: {new Date(data?.generated_at || Date.now()).toLocaleDateString('es-CL')}.</li>
                  </ul>
                  <p className="text-xs text-slate-500 italic mb-3">
                    <strong className="text-slate-400">Cita sugerida:</strong> Estudio Reto 21 Días sin Drogas. SinAdicciones.org, {new Date().toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}. Datos agregados y anonimizados de {total} participantes voluntarios.
                  </p>
                  <p className="text-xs text-slate-500">
                    Contacto prensa: <a href="mailto:contacto@sinadicciones.org" className="text-emerald-400 hover:underline">contacto@sinadicciones.org</a>
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* ============== LOADING / NOT READY ============== */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        )}
        {!loading && !ready && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <Card className="bg-slate-900/60 border-slate-800" data-testid="not-ready-card">
              <CardContent className="p-10 text-center">
                <Users className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-3">El estudio se publicará pronto</h2>
                <p className="text-slate-300 max-w-xl mx-auto">
                  Por rigurosidad estadística, los datos se publican al alcanzar 100 participantes.
                  {total > 0 && (
                    <> Actualmente: <span className="text-emerald-400 font-semibold">{total}</span>.</>
                  )}
                </p>
                <p className="text-sm text-slate-400 mt-4">
                  ¿Quieres aportar? Empieza el Reto 21 Días y serás parte del estudio (de forma anónima).
                </p>
                <div className="mt-6">
                  <Link to="/reto-21-dias">
                    <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600" data-testid="cta-join-study">
                      Sumarme al Reto 21 Días
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* ============== CTA FINAL ============== */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Te identificas con estos datos?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              No estás solo. El Reto 21 Días es un plan personalizado, gratuito, basado en tu sustancia, tus gatillos y tus fortalezas. Empezar toma 3 minutos.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/reto-21-dias">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="cta-final-start">
                  Empezar mi Reto 21 Días
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              {ready && (
                <a
                  href={downloadPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="inline-flex items-center justify-center rounded-md border border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 h-11 px-8 text-sm font-medium transition-colors"
                  data-testid="cta-final-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar 1-pager (prensa)
                </a>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
